/**
 * G02. FSM Node-State 敵人 AI — generic finite state machine.
 *
 * Inspired by Godot 4 GDQuest FSM tutorial. Each state owns its enter/update/exit
 * lifecycle; the StateMachine just routes calls to `current`. Transition logic
 * lives inside the states themselves (they may call `ctx.sm.transitionTo(...)`),
 * or externally from the owning entity.
 *
 * Design rules:
 * - States are STATELESS singletons where possible (state instances should not
 *   hold mutable data; mutable data lives on TContext). This keeps re-entry safe.
 * - `enter(ctx)` is called on transition INTO the state; `exit(ctx)` on transition
 *   OUT. Both run synchronously, exit-first.
 * - `update(ctx, dtSec)` runs every frame on the current state only.
 * - Transitioning to the same state name is a no-op (no exit/enter cycle) — this
 *   prevents accidental flicker when callers re-trigger the same logical state.
 * - Transitioning to an unknown state name throws (fail-fast for typos).
 */

export interface IState<TContext = unknown> {
  /** Unique name for lookup. Use lowercase-kebab convention. */
  readonly name: string;
  /** Called once when the state becomes current. */
  enter(ctx: TContext): void;
  /** Called every frame while this state is current. dtSec is delta in seconds. */
  update(ctx: TContext, dtSec: number): void;
  /** Called once when transitioning away from this state. */
  exit(ctx: TContext): void;
}

export class StateMachine<TContext = unknown> {
  private current: IState<TContext> | null = null;
  private readonly states = new Map<string, IState<TContext>>();
  private readonly ctx: TContext;

  constructor(ctx: TContext) {
    this.ctx = ctx;
  }

  /** Register a state by its name. Returns self for chaining. */
  add(state: IState<TContext>): this {
    if (this.states.has(state.name)) {
      throw new Error(`StateMachine.add: duplicate state name "${state.name}"`);
    }
    this.states.set(state.name, state);
    return this;
  }

  /**
   * Transition to a registered state by name.
   * - exit(current) → enter(next) order is guaranteed.
   * - Transitioning to the current state name is a no-op.
   * - Unknown state name throws.
   */
  transitionTo(name: string): void {
    if (this.current && this.current.name === name) return;
    const next = this.states.get(name);
    if (!next) {
      throw new Error(
        `StateMachine.transitionTo: unknown state "${name}" (registered: ${Array.from(
          this.states.keys(),
        ).join(', ')})`,
      );
    }
    if (this.current) this.current.exit(this.ctx);
    this.current = next;
    next.enter(this.ctx);
  }

  /** Run one frame of the current state. No-op if no state has been entered yet. */
  update(dtSec: number): void {
    if (this.current) this.current.update(this.ctx, dtSec);
  }

  /** Name of the current state, or undefined if none entered yet. */
  get currentName(): string | undefined {
    return this.current?.name;
  }

  /** True if the named state has been registered. */
  has(name: string): boolean {
    return this.states.has(name);
  }

  /** Number of registered states. */
  get size(): number {
    return this.states.size;
  }
}
